import React, { useState, useEffect } from 'react';
import './ApprovalStatistics.css';
import axios from 'axios';
import { api } from '../config';
import Spinner from './Spinner';
import '../styles/ResizableTable.css';
import { initResizableTables } from '../utils/resizableTables';

interface StatisticsData {
  total_requests: number;
  total_approvals: number;
  total_rejected: number;
  dq_approvals: number;
  pending_count: number;
  delegated_count: number;
  average_approval_time_days: number;
  met_sla_count: number;
  missed_sla_count: number;
  sla_compliance_rate: number;
  approvals_by_day: { [key: string]: number };
}

interface InitiativeWithStatus {
  id: number;
  use_case_name: string;
  submitted_by: string;
  submitted_date: string;
  proposed_solution_owner?: string;
  solution_sponsor?: string;
  purpose?: string;
  value?: string;
  users?: string;
  solution_scope?: string;
  proposed_workflow?: string;
  inputs_and_data_sources?: string;
  approved_by?: string;
  status: 'pending' | 'approved' | 'rejected';
  dq_status?: 'pending' | 'approved' | 'rejected';
  dq_approved_by?: string;
  dq_reviewed_by?: string;
  technical_stack_frontend?: string;
  technical_stack_ai?: string;
  structured_data_source?: string;
  unstructured_data_sources?: string;
  user_base?: string;
  evaluated_existing_solutions?: string;
  security_considerations?: string;
  security_classification?: string;
  third_party_components?: string;
  reviewer_comment?: string;
}

const ApprovalStatistics: React.FC = () => {
  const [statistics, setStatistics] = useState<StatisticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initiatives, setInitiatives] = useState<InitiativeWithStatus[]>([]);
  const [initiativesLoading, setInitiativesLoading] = useState(false);

  useEffect(() => {
    if (!isLoading && !initiativesLoading) {
      setTimeout(() => initResizableTables(), 50);
    }
  }, [isLoading, initiativesLoading]);

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await axios.get(api('/api/initiatives/statistics/summary'));
        setStatistics(response.data);
      } catch (err: any) {
        setError('Failed to load statistics');
        console.error('Error fetching statistics:', err);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchInitiatives = async () => {
      try {
        setInitiativesLoading(true);
        // Fetch all initiatives with full details
        const initiativesRes = await axios.get(api('/api/initiatives'));
        
        // Fetch DQ reviews to get DQ status and details
        const dqRes = await axios.get(api('/api/data-quality-reviews/submitted/list'));
        
        // Merge data
        const initiativesWithStatus = initiativesRes.data.map((initiative: any) => {
          const dqReview = dqRes.data.find((review: any) => review.initiative_id === initiative.id);
          return {
            id: initiative.id,
            use_case_name: initiative.use_case_name,
            submitted_by: initiative.submitted_by,
            submitted_date: initiative.submitted_date,
            proposed_solution_owner: initiative.proposed_solution_owner,
            solution_sponsor: initiative.solution_sponsor,
            purpose: initiative.purpose,
            value: initiative.value,
            users: initiative.users,
            solution_scope: initiative.solution_scope,
            proposed_workflow: initiative.proposed_workflow,
            inputs_and_data_sources: initiative.inputs_and_data_sources,
            approved_by: initiative.approved_by,
            status: initiative.status || 'pending',
            dq_status: dqReview?.review_status || 'pending',
            dq_approved_by: dqReview?.dq_approved_by,
            dq_reviewed_by: dqReview?.reviewed_by,
            technical_stack_frontend: dqReview?.technical_stack_frontend,
            technical_stack_ai: dqReview?.technical_stack_ai,
            structured_data_source: dqReview?.structured_data_source,
            unstructured_data_sources: dqReview?.unstructured_data_sources,
            user_base: dqReview?.user_base,
            evaluated_existing_solutions: dqReview?.evaluated_existing_solutions,
            security_considerations: dqReview?.security_considerations,
            security_classification: dqReview?.security_classification,
            third_party_components: dqReview?.third_party_components,
            reviewer_comment: dqReview?.reviewer_comment,
          };
        });
        
        setInitiatives(initiativesWithStatus);
      } catch (err: any) {
        console.error('Error fetching initiatives:', err);
      } finally {
        setInitiativesLoading(false);
      }
    };

    fetchStatistics();
    fetchInitiatives();
  }, []);

  if (isLoading) {
    return (
      <div className="statistics-container">
        <Spinner message="Loading statistics..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="statistics-container">
        <div className="error">{error}</div>
      </div>
    );
  }

  if (!statistics) {
    return (
      <div className="statistics-container">
        <div className="no-data">No statistics available</div>
      </div>
    );
  }

  const getSLAStatusClass = (rate: number) => {
    if (rate >= 80) return 'sla-excellent';
    if (rate >= 60) return 'sla-good';
    if (rate >= 40) return 'sla-fair';
    return 'sla-poor';
  };

  const calculateDaysRemaining = (submittedDate: string) => {
    const submitted = new Date(submittedDate);
    const now = new Date();
    const diffMs = now.getTime() - submitted.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return Math.max(0, 7 - diffDays);
  };

  const calculateDaysSince = (submittedDate: string) => {
    const submitted = new Date(submittedDate);
    const now = new Date();
    const diffMs = now.getTime() - submitted.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  };

  const handleExportToExcel = () => {
    // Get status color based on status value
    const getStatusColor = (status: string): string => {
      switch(status.toLowerCase()) {
        case 'approved': return '#C6EFCE';
        case 'pending': return '#FFEB9C';
        case 'rejected': return '#FFC7CE';
        default: return '#FFFFFF';
      }
    };

    // Get DQ status color
    const getDQStatusColor = (dqStatus: string): string => {
      switch(dqStatus.toLowerCase()) {
        case 'approved': return '#C6EFCE';
        case 'pending': return '#FFF8DC';
        case 'rejected': return '#FFC7CE';
        default: return '#FFFFFF';
      }
    };

    // Create HTML for Excel export - matching exactly what's shown on screen
    let htmlContent = `
      <html xmlns:x="urn:schemas-microsoft-com:office:excel">
      <head>
        <meta charset="UTF-8">
        <style>
          body {
            font-family: Arial, sans-serif;
          }
          h2, h3 {
            color: #ee0000;
            margin-top: 20px;
            margin-bottom: 10px;
          }
          h3 {
            font-size: 14px;
            margin-top: 15px;
          }
          table {
            border-collapse: collapse;
            width: 100%;
            font-family: Arial, sans-serif;
            font-size: 11px;
            margin-bottom: 20px;
          }
          th {
            background-color: #f8f9fa;
            color: #333;
            border: 1px solid #ddd;
            padding: 8px;
            text-align: center;
            font-weight: 600;
            font-size: 11px;
          }
          td {
            border: 1px solid #eee;
            padding: 8px;
            text-align: center;
            font-size: 12px;
          }
          td.text-left {
            text-align: left;
          }
          .status-approved {
            background-color: #C6EFCE;
            font-weight: bold;
          }
          .status-pending {
            background-color: #FFEB9C;
            font-weight: bold;
          }
          .status-rejected {
            background-color: #FFC7CE;
            font-weight: bold;
          }
          .dq-section {
            background-color: #fff8f0;
            margin-left: 20px;
            margin-top: 10px;
          }
          .dq-section th {
            background-color: #fff3e0;
          }
        </style>
      </head>
      <body>
        <h2>AI Repo Pre-Board Initiatives Export</h2>
        <p>Exported on: ${new Date().toLocaleString()}</p>
    `;

    // PENDING INITIATIVES
    const pendingInitiatives = initiatives.filter(i => i.status === 'pending');
    if (pendingInitiatives.length > 0) {
      htmlContent += `<h3>⏳ Pending Approval (${pendingInitiatives.length})</h3>`;
      htmlContent += `
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Initiative Name</th>
              <th>Submitted By</th>
              <th>Owner</th>
              <th>Status</th>
              <th>Submitted Date</th>
              <th>Days Since</th>
              <th>Days Remaining (SLA: 7)</th>
            </tr>
          </thead>
          <tbody>
      `;
      
      pendingInitiatives.forEach((initiative) => {
        htmlContent += `
          <tr>
            <td>#${initiative.id}</td>
            <td class="text-left">${initiative.use_case_name || ''}</td>
            <td class="text-left">${initiative.submitted_by || ''}</td>
            <td class="text-left">${initiative.proposed_solution_owner || 'N/A'}</td>
            <td class="status-pending">PENDING</td>
            <td>${new Date(initiative.submitted_date).toLocaleDateString()}</td>
            <td>${calculateDaysSince(initiative.submitted_date)}</td>
            <td>${calculateDaysRemaining(initiative.submitted_date)}</td>
          </tr>
        `;
      });
      
      htmlContent += `
          </tbody>
        </table>
      `;
    }

    // APPROVED INITIATIVES
    const approvedInitiatives = initiatives.filter(i => i.status === 'approved');
    if (approvedInitiatives.length > 0) {
      htmlContent += `<h3>✓ Approved (${approvedInitiatives.length})</h3>`;
      
      approvedInitiatives.forEach((initiative) => {
        // Approval table
        htmlContent += `
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Initiative Name</th>
                <th>Submitted By</th>
                <th>Owner</th>
                <th>Status</th>
                <th>Submitted Date</th>
                <th>Days Since</th>
                <th>Days Remaining (SLA: 7)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>#${initiative.id}</td>
                <td class="text-left">${initiative.use_case_name || ''}</td>
                <td class="text-left">${initiative.submitted_by || ''}</td>
                <td class="text-left">${initiative.proposed_solution_owner || 'N/A'}</td>
                <td class="status-approved">APPROVED</td>
                <td>${new Date(initiative.submitted_date).toLocaleDateString()}</td>
                <td>${calculateDaysSince(initiative.submitted_date)}</td>
                <td>${calculateDaysRemaining(initiative.submitted_date)}</td>
              </tr>
            </tbody>
          </table>
        `;

        // DQ Review table
        htmlContent += `
          <table class="dq-section">
            <thead>
              <tr>
                <th>ID</th>
                <th>Initiative Name</th>
                <th>Submitted By</th>
                <th>DQ Reviewed By</th>
                <th>Review Date</th>
                <th>DQ Review Status</th>
                <th>DQ Review Approved By</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>#${initiative.id}</td>
                <td class="text-left">${initiative.use_case_name || ''}</td>
                <td class="text-left">${initiative.submitted_by || ''}</td>
                <td class="text-left">${initiative.dq_reviewed_by || 'N/A'}</td>
                <td>${initiative.dq_reviewed_by ? new Date(initiative.submitted_date).toLocaleDateString() : 'N/A'}</td>
                <td class="${initiative.dq_status === 'approved' ? 'status-approved' : initiative.dq_status === 'pending' ? 'status-pending' : 'status-rejected'}">${initiative.dq_status.toUpperCase()}</td>
                <td class="text-left">${initiative.dq_approved_by || 'N/A'}</td>
              </tr>
            </tbody>
          </table>
        `;
      });
    }

    // REJECTED INITIATIVES
    const rejectedInitiatives = initiatives.filter(i => i.status === 'rejected');
    if (rejectedInitiatives.length > 0) {
      htmlContent += `<h3>❌ Rejected (${rejectedInitiatives.length})</h3>`;
      htmlContent += `
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>Initiative Name</th>
              <th>Submitted By</th>
              <th>Owner</th>
              <th>Status</th>
              <th>Submitted Date</th>
              <th>Days Since</th>
              <th>Days Remaining (SLA: 7)</th>
            </tr>
          </thead>
          <tbody>
      `;
      
      rejectedInitiatives.forEach((initiative) => {
        htmlContent += `
          <tr>
            <td>#${initiative.id}</td>
            <td class="text-left">${initiative.use_case_name || ''}</td>
            <td class="text-left">${initiative.submitted_by || ''}</td>
            <td class="text-left">${initiative.proposed_solution_owner || 'N/A'}</td>
            <td class="status-rejected">REJECTED</td>
            <td>${new Date(initiative.submitted_date).toLocaleDateString()}</td>
            <td>${calculateDaysSince(initiative.submitted_date)}</td>
            <td>${calculateDaysRemaining(initiative.submitted_date)}</td>
          </tr>
        `;
      });
      
      htmlContent += `
          </tbody>
        </table>
      `;
    }

    htmlContent += `
      </body>
      </html>
    `;

    // Create blob and download
    const blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `initiatives_${new Date().toISOString().split('T')[0]}.xls`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="statistics-container">

      {/* Top Row - Overall Counts */}
      <div className="stats-grid top-row">
        <div className="stat-card">
          <div className="stat-icon">📋</div>
          <div className="stat-content">
            <div className="stat-label">Total Requests</div>
            <div className="stat-value">{statistics.total_requests}</div>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">✓</div>
          <div className="stat-content">
            <div className="stat-label">Total Approvals</div>
            <div className="stat-value">{statistics.total_approvals}</div>
          </div>
        </div>

        <div className="stat-card success">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-label">DQ Approvals</div>
            <div className="stat-value">{statistics.dq_approvals}</div>
          </div>
        </div>

        <div className="stat-card error">
          <div className="stat-icon">❌</div>
          <div className="stat-content">
            <div className="stat-label">Total Rejected</div>
            <div className="stat-value">{statistics.total_rejected}</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <div className="stat-label">Pending Approval</div>
            <div className="stat-value">{statistics.pending_count}</div>
          </div>
        </div>
      </div>

      {/* Bottom Row - SLA Metrics */}
      <div className="stats-grid bottom-row">
        {/* SLA Compliance Rate */}
        <div className={`metric-card ${getSLAStatusClass(statistics.sla_compliance_rate)}`}>
          <div className="metric-header">
            <div className="metric-icon">📊</div>
            <div className="metric-title">Overall SLA Compliance</div>
          </div>
          <div className="metric-percentage">{statistics.sla_compliance_rate.toFixed(1)}%</div>
          <div className="metric-description">7-Day Target</div>
        </div>

        {/* Met SLA */}
        <div className="metric-card met-sla">
          <div className="metric-header">
            <div className="metric-icon">✓</div>
            <div className="metric-title">Met SLA</div>
          </div>
          <div className="metric-percentage">{statistics.met_sla_count}</div>
          <div className="metric-description">≤ 7 days</div>
        </div>

        {/* Missed SLA */}
        <div className="metric-card missed-sla">
          <div className="metric-header">
            <div className="metric-icon">⚠️</div>
            <div className="metric-title">Missed SLA</div>
          </div>
          <div className="metric-percentage">{statistics.missed_sla_count}</div>
          <div className="metric-description">&gt; 7 days</div>
        </div>

        {/* Delegated Requests */}
        <div className="metric-card">
          <div className="metric-header">
            <div className="metric-icon">🔀</div>
            <div className="metric-title">Delegated Requests</div>
          </div>
          <div className="metric-percentage">{statistics.delegated_count}</div>
          <div className="metric-description">Active Delegations</div>
        </div>
      </div>

      {/* Initiatives List Section */}
      <div className="initiatives-section">
        <div className="initiatives-header">
          <h2>All Initiatives</h2>
          <button className="btn-export" onClick={handleExportToExcel}>
            📥 Export to Excel
          </button>
        </div>

        {/* Pending Initiatives */}
        <div className="initiatives-category">
          <h3 className="category-title pending">⏳ Pending Approval ({initiatives.filter(i => i.status === 'pending').length})</h3>
          {initiatives.filter(i => i.status === 'pending').length === 0 ? (
            <div className="no-initiatives">No pending initiatives</div>
          ) : (
            <div className="initiatives-table-wrapper">
              <table className="initiatives-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Initiative Name</th>
                    <th>Submitted By</th>
                    <th>Owner</th>
                    <th>Status</th>
                    <th>Submitted Date</th>
                    <th>Days Since</th>
                    <th>Days Remaining (SLA: 7)</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {initiatives.filter(i => i.status === 'pending').map((initiative) => (
                    <tr key={initiative.id} className="initiative-row">
                      <td className="id-cell">#{initiative.id}</td>
                      <td className="name-cell">{initiative.use_case_name}</td>
                      <td>{initiative.submitted_by}</td>
                      <td>{initiative.proposed_solution_owner || 'N/A'}</td>
                      <td><span className="status-badge pending">PENDING</span></td>
                      <td>{new Date(initiative.submitted_date).toLocaleDateString()}</td>
                      <td className="days-cell">{calculateDaysSince(initiative.submitted_date)}</td>
                      <td className="days-cell">{calculateDaysRemaining(initiative.submitted_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Approved Initiatives */}
        <div className="initiatives-category">
          <h3 className="category-title approved">✓ Approved ({initiatives.filter(i => i.status === 'approved').length})</h3>
          {initiatives.filter(i => i.status === 'approved').length === 0 ? (
            <div className="no-initiatives">No approved initiatives</div>
          ) : (
            <div className="approved-table-container">
              {initiatives.filter(i => i.status === 'approved').map((initiative) => (
                <div key={initiative.id} className="initiative-table-pair">
                  {/* Initiative Approval Row */}
                  <div className="initiatives-table-wrapper">
                    <table className="initiatives-table initiatives-approval-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Initiative Name</th>
                          <th>Submitted By</th>
                          <th>Owner</th>
                          <th>Status</th>
                          <th>Submitted Date</th>
                          <th>Days Since</th>
                          <th>Days Remaining (SLA: 7)</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="initiative-row">
                          <td className="id-cell">#{initiative.id}</td>
                          <td className="name-cell">{initiative.use_case_name}</td>
                          <td>{initiative.submitted_by}</td>
                          <td>{initiative.proposed_solution_owner || 'N/A'}</td>
                          <td><span className="status-badge approved">APPROVED</span></td>
                          <td>{new Date(initiative.submitted_date).toLocaleDateString()}</td>
                          <td className="days-cell">{calculateDaysSince(initiative.submitted_date)}</td>
                          <td className="days-cell">{calculateDaysRemaining(initiative.submitted_date)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* DQ Review Row */}
                  <div className="initiatives-table-wrapper dq-table-wrapper">
                    <table className="initiatives-table dq-review-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Initiative Name</th>
                          <th>Submitted By</th>
                          <th>DQ Reviewed By</th>
                          <th>Review Date</th>
                          <th>DQ Review Status</th>
                          <th>DQ Review Approved By</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="dq-row">
                          <td className="id-cell">#{initiative.id}</td>
                          <td className="name-cell">{initiative.use_case_name}</td>
                          <td>{initiative.submitted_by}</td>
                          <td>{initiative.dq_reviewed_by || 'N/A'}</td>
                          <td>{initiative.dq_reviewed_by ? new Date(initiative.submitted_date).toLocaleDateString() : 'N/A'}</td>
                          <td>
                            {initiative.dq_status === 'pending' ? (
                              <span className="status-badge pending">PENDING</span>
                            ) : initiative.dq_status === 'approved' ? (
                              <span className="status-badge approved">APPROVED</span>
                            ) : (
                              <span className="status-badge rejected">REJECTED</span>
                            )}
                          </td>
                          <td>{initiative.dq_approved_by || 'N/A'}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Rejected Initiatives */}
        <div className="initiatives-category">
          <h3 className="category-title rejected">❌ Rejected ({initiatives.filter(i => i.status === 'rejected').length})</h3>
          {initiatives.filter(i => i.status === 'rejected').length === 0 ? (
            <div className="no-initiatives">No rejected initiatives</div>
          ) : (
            <div className="initiatives-table-wrapper">
              <table className="initiatives-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Initiative Name</th>
                    <th>Submitted By</th>
                    <th>Owner</th>
                    <th>Status</th>
                    <th>Submitted Date</th>
                    <th>Days Since</th>
                    <th>Days Remaining (SLA: 7)</th>
                  </tr>
                </thead>
                <tbody>
                  {initiatives.filter(i => i.status === 'rejected').map((initiative) => (
                    <tr key={initiative.id} className="initiative-row">
                      <td className="id-cell">#{initiative.id}</td>
                      <td className="name-cell">{initiative.use_case_name}</td>
                      <td>{initiative.submitted_by}</td>
                      <td>{initiative.proposed_solution_owner || 'N/A'}</td>
                      <td><span className="status-badge rejected">REJECTED</span></td>
                      <td>{new Date(initiative.submitted_date).toLocaleDateString()}</td>
                      <td className="days-cell">{calculateDaysSince(initiative.submitted_date)}</td>
                      <td className="days-cell">{calculateDaysRemaining(initiative.submitted_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApprovalStatistics;
